/**
 * The Agents section's connections (/agents/connections, /agents/connections/:id): a tenant's
 * own connections to Datadog, GitHub, Linear, Slack and any MCP server, which agents name in
 * their triggers, tools and approval gates.
 *
 * Secrets are write-only (src/api/routers/agent_connections.py): typed here, sent once to
 * Secrets Manager, never returned. Replace one by typing it again. A connection that takes
 * deliveries shows the URL to give the provider.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug } from 'lucide-react';
import ApiService from '@/services/api.service';
import { useUserRoles } from '@/hooks/useUserRoles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { showToast } from '@/services/toast.service';
import { gitService } from '@/services/git.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { RETURN_TO_KEY } from '@/pages/githubCallbackStep';
import { DocsMarkdown } from '@/pages/docs/DocsMarkdown';
import type { DocContent } from '@/pages/docs/docsData';
import { CONNECTORS, PLIABLE, mcpTools, type Connection, type TeamDoc } from './catalog';
import { AGENTS_PATH, connectionPath, errorText, teamPath, useConnections, useTeams } from './data';

const api = () => ApiService.getInstance();

// Each connector's setup guide is its Docs page (Docs → Agent Connections), shown beside the form.
const guides = import.meta.glob<{ default: DocContent }>('../docs/content/agent-connection-*.ts', { eager: true });

function SetupGuide({ connector }: { connector: string }) {
  const doc = guides[`../docs/content/agent-connection-${connector}.ts`]?.default;
  return (
    <aside className="w-[480px] flex-shrink-0 border-l overflow-auto p-6 text-sm">
      {doc ? (
        <>
          <h2 className="text-lg font-semibold mb-4">Setting up {doc.title}</h2>
          <DocsMarkdown content={doc.content} className="[&_h2]:text-base [&_h2]:mt-6" />
        </>
      ) : (
        <p className="text-muted-foreground">Choose a connector to see how to set it up.</p>
      )}
    </aside>
  );
}

const randomSecret = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');

function SecretFields({ connector, onSubmit, submitLabel, pending }: {
  connector: string;
  onSubmit: (secret: Record<string, string>) => void;
  submitLabel: string;
  pending: boolean;
}) {
  const fields = CONNECTORS[connector]?.fields ?? [];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, f.generated ? randomSecret() : ''])),
  );
  const complete = fields.every((f) => values[f.key]?.trim());
  return (
    <div className="space-y-2">
      {fields.map((f) => (
        <div key={f.key} className="space-y-1">
          <Label htmlFor={`${connector}-${f.key}`}>{f.label}</Label>
          <Input
            id={`${connector}-${f.key}`}
            type={f.generated ? 'text' : 'password'}
            autoComplete="off"
            value={values[f.key] ?? ''}
            onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
          />
        </div>
      ))}
      <Button size="sm" disabled={!complete || pending} onClick={() => onSubmit(values)}>
        {pending ? 'Saving…' : submitLabel}
      </Button>
    </div>
  );
}

/** A GitHub connection through the Pliable GitHub app (Settings → Git): no token to paste. */
function GithubApp({ existing, pending, onSubmit }: { existing?: Connection; pending: boolean; onSubmit: () => void }) {
  const repos = useQuery({ queryKey: ['github-repos'], queryFn: () => gitService.getAvailableRepos() });
  const [installing, setInstalling] = useState(false);
  const install = async () => {
    setInstalling(true);
    try {
      const res = (await api().request('POST', '/github/install-url')) as { url: string };
      try {
        sessionStorage.setItem(RETURN_TO_KEY, `${AGENTS_PATH}/connections?connector=github`);
      } catch {
        // Without it the callback lands on Settings → Git, which still works.
      }
      window.location.href = res.url;
    } catch (e) {
      setInstalling(false);
      showToast.error(errorText(e, 'Could not start the GitHub install.'));
    }
  };
  if (existing)
    return (
      <p className="text-sm text-muted-foreground">
        <Link to={connectionPath(existing.id)} className="underline">
          {existing.name}
        </Link>{' '}
        already uses the Pliable GitHub app. Give steps that connection, each with its own repositories.
      </p>
    );
  if (repos.isLoading) return <p className="text-sm text-muted-foreground">Checking the GitHub app…</p>;
  if (!repos.data?.length)
    return (
      <div className="space-y-2 text-sm">
        <p className="text-muted-foreground">The Pliable GitHub app isn’t installed for this workspace yet. Install it, choosing the repositories agents may read, then come back here.</p>
        <Button size="sm" variant="outline" disabled={installing} onClick={install}>
          {installing ? 'Opening GitHub…' : 'Install the GitHub app'}
        </Button>
      </div>
    );
  return (
    <div className="space-y-2 text-sm">
      <p className="text-muted-foreground">
        Installed. Agents can be given these repositories, read-only: {repos.data.map((r) => r.full_name).join(', ')}. To add one, change the
        app’s repositories in GitHub.
      </p>
      <Button size="sm" disabled={pending} onClick={onSubmit}>
        {pending ? 'Saving…' : 'Add connection'}
      </Button>
    </div>
  );
}

/** A Linear connection through the Pliable Linear app: installed in Linear with one click, posting as the app. */
function LinearApp({ existing, name }: { existing?: Connection; name: string }) {
  const install = useMutation({
    mutationFn: async () => (await api().request('POST', '/agent-connections/linear-app/install', { name })) as { url: string },
    // Linear returns to /agents/connections/linear/callback (LinearAppCallback), which creates the connection.
    onSuccess: (res) => (window.location.href = res.url),
    onError: (e) => showToast.error(errorText(e, 'Could not start the Linear install.')),
  });
  if (existing)
    return (
      <p className="text-sm text-muted-foreground">
        <Link to={connectionPath(existing.id)} className="underline">
          {existing.name}
        </Link>{' '}
        already uses the Pliable Linear app. Give steps that connection, each with its own teams.
      </p>
    );
  return (
    <div className="space-y-2 text-sm">
      <p className="text-muted-foreground">Install the Pliable app in your Linear workspace. Tickets and comments post as the app, not as you.</p>
      <Button size="sm" disabled={install.isPending || install.isSuccess} onClick={() => install.mutate()}>
        {install.isPending || install.isSuccess ? 'Opening Linear…' : 'Install in Linear'}
      </Button>
    </div>
  );
}

/** An MCP server that signs in with OAuth: Pliable registers itself there unless the server needs a client made by hand. */
function McpSignIn({ name, url }: { name: string; url: string }) {
  const [client, setClient] = useState({ client_id: '', client_secret: '' });
  const signIn = useMutation({
    mutationFn: async () =>
      (await api().request('POST', '/agent-connections/mcp/install', {
        name, url, client_id: client.client_id.trim() || null, client_secret: client.client_secret.trim() || null,
      })) as { url: string },
    // The server returns to /agents/connections/mcp/callback (McpCallback), which creates the connection.
    onSuccess: (res) => (window.location.href = res.url),
    onError: (e) => showToast.error(errorText(e, 'Could not start the sign-in.')),
  });
  return (
    <div className="space-y-2 text-sm">
      <p className="text-muted-foreground">
        Sign in to the server and approve Pliable. Most servers register Pliable themselves; if yours asks for an OAuth client, create one
        with redirect URL <code className="text-xs">{`${window.location.origin}${AGENTS_PATH}/connections/mcp/callback`}</code> and paste it here.
      </p>
      <div className="flex gap-2">
        <Input aria-label="Client ID (optional)" placeholder="Client ID (optional)" className="w-56" value={client.client_id}
          onChange={(e) => setClient({ ...client, client_id: e.target.value })} />
        <Input aria-label="Client secret (optional)" placeholder="Client secret (optional)" type="password" autoComplete="off" className="w-56"
          value={client.client_secret} onChange={(e) => setClient({ ...client, client_secret: e.target.value })} />
      </div>
      <Button size="sm" disabled={!url.trim() || signIn.isPending || signIn.isSuccess} onClick={() => signIn.mutate()}>
        {signIn.isPending || signIn.isSuccess ? 'Opening the server…' : 'Sign in'}
      </Button>
    </div>
  );
}

/** What an MCP server offers, as it listed it; Admins list it again after the server changes. */
function McpTools({ c, isAdmin }: { c: Connection; isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const refresh = useMutation({
    mutationFn: async () => api().request('POST', `/agent-connections/${c.id}/tools`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agent-connections'] }),
    onError: (e) => showToast.error(errorText(e, 'Could not list the server’s tools.')),
  });
  const tools = mcpTools(c);
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold">Tools</h2>
        {isAdmin && (
          <Button size="sm" variant="outline" className="ml-auto" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
            {refresh.isPending ? 'Refreshing…' : 'Refresh tools'}
          </Button>
        )}
      </div>
      <p className="text-muted-foreground">
        From <code className="text-xs break-all">{String(c.config?.url ?? '')}</code>. Each step picks the ones it may call. “Reads only” is the
        server’s own claim.
      </p>
      <ul className="space-y-1">
        {tools.map((t) => (
          <li key={t.name}>
            <code className="text-xs">{t.name}</code>
            {t.read_only && <span className="ml-2 text-xs text-muted-foreground">reads only</span>}
            {t.description && <p className="text-muted-foreground text-xs line-clamp-2">{t.description}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Where Linear returns after the Pliable Linear app is installed. */
export const LinearAppCallback = () => <OAuthCallback provider="Linear" connector="linear" completePath="/agent-connections/linear-app/complete" />;
/** Where an MCP server's authorization server returns after the admin signs in. */
export const McpCallback = () => <OAuthCallback provider="the MCP server" connector="mcp" completePath="/agent-connections/mcp/complete" />;

/** Where a provider returns after the admin authorizes Pliable: the backend exchanges the code and creates the connection. */
function OAuthCallback({ provider, connector, completePath }: { provider: string; connector: string; completePath: string }) {
  const apiReady = useApiAuthReady();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const code = params.get('code');
  const state = params.get('state');
  const denied = params.get('error');
  // A code and state work once, so this is asked exactly once.
  const done = useQuery({
    queryKey: ['agent-connection-install', connector, state],
    queryFn: async () => (await api().request('POST', completePath, { code, state })) as Connection,
    enabled: apiReady && !!code && !!state && !denied,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  useEffect(() => {
    if (!done.data) return;
    queryClient.invalidateQueries({ queryKey: ['agent-connections'] });
    navigate(connectionPath(done.data.id), { replace: true });
  }, [done.data, navigate, queryClient]);
  return (
    <div className="p-8 space-y-4 text-sm">
      {denied || !code || !state ? (
        <p className="text-destructive">The sign-in wasn’t completed in {provider}{denied ? ` (${denied})` : ''}.</p>
      ) : done.error ? (
        <p className="text-destructive">{errorText(done.error, 'The install failed.')}</p>
      ) : (
        <p className="text-muted-foreground">Finishing the connection…</p>
      )}
      <Link to={`${AGENTS_PATH}/connections?connector=${connector}`} className="underline">
        Back to Connections
      </Link>
    </div>
  );
}

/** The page header both connection pages share. */
function Header({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="px-6 py-4 page-header-surface flex-shrink-0 flex items-center gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold truncate">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="ml-auto flex gap-2">{actions}</div>
    </div>
  );
}

/** The agents that name this connection, and how. */
function usedBy(teams: TeamDoc[], name: string) {
  return teams.flatMap((t) => {
    const how = new Set<string>();
    for (const m of t.members) {
      if (m.triggers.some((tr) => tr.connection === name)) how.add('starts on it');
      if (m.capabilities.some((c) => c.connection === name)) how.add('uses it as a tool');
    }
    if (t.edges.some((e) => e.gate?.connection === name)) how.add('asks for approval through it');
    return how.size ? [{ team: t, how: [...how] }] : [];
  });
}

export function ConnectionDetail() {
  const { connectionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replacing, setReplacing] = useState(false);
  const [newName, setNewName] = useState<string | null>(null); // set while renaming
  // Builders see connections (their teams name them); only Admins add, re-key or remove one.
  const { isAdmin } = useUserRoles();
  const connections = useConnections();
  const teams = useTeams();
  const c = connections.data?.connections.find((x) => x.id === connectionId);
  const replace = useMutation({
    mutationFn: async (secret: Record<string, string>) => api().request('PUT', `/agent-connections/${connectionId}/secret`, { secret }),
    onSuccess: () => {
      setReplacing(false);
      showToast.success('Secret replaced');
    },
    onError: (e) => showToast.error(errorText(e, 'Could not replace the secret.')),
  });
  const rename = useMutation({
    mutationFn: async (name: string) => api().request('PATCH', `/agent-connections/${connectionId}`, { name }),
    onSuccess: () => {
      setNewName(null);
      // Steps hold it by id; their documents show it by name.
      queryClient.invalidateQueries({ queryKey: ['agent-connections'] });
      queryClient.invalidateQueries({ queryKey: ['agent-teams'] });
    },
    onError: (e) => showToast.error(errorText(e, 'Could not rename the connection.')),
  });
  const remove = useMutation({
    mutationFn: async () => api().request('DELETE', `/agent-connections/${connectionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-connections'] });
      navigate(`${AGENTS_PATH}/connections`);
    },
    onError: (e) => showToast.error(errorText(e, 'Could not delete the connection.')),
  });

  if (connections.isLoading) return <p className="p-8 text-sm text-muted-foreground">Loading…</p>;
  if (!c) return <p className="p-8 text-sm text-destructive">Connection not found.</p>;
  const spec = CONNECTORS[c.connector];
  const users = usedBy(teams.data?.teams ?? [], c.name);
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <Header
        title={c.name}
        subtitle={spec?.label ?? c.connector}
        actions={
          isAdmin &&
          (newName !== null ? (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (newName.trim()) rename.mutate(newName.trim());
              }}
            >
              <Input aria-label="Connection name" className="w-56" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} />
              <Button type="submit" disabled={!newName.trim() || rename.isPending}>
                {rename.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setNewName(null)}>
                Cancel
              </Button>
            </form>
          ) : (
            <>
              <Button variant="outline" onClick={() => setNewName(c.name)}>
                Rename
              </Button>
              <Button
                variant="ghost"
                className="text-destructive"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Delete ${c.name}? Its secret is scheduled for deletion.`)) remove.mutate();
                }}
              >
                Delete
              </Button>
            </>
          ))
        }
      />
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 overflow-auto">
          <div className="max-w-3xl p-6 space-y-8 text-sm">
            {spec?.hint && <p className="text-muted-foreground">{spec.hint}</p>}
            {c.delivery_url && (
              <section className="space-y-2">
                <h2 className="font-semibold">Delivery URL</h2>
                <p className="text-muted-foreground">Where {spec?.label ?? 'the provider'} sends events for this connection.</p>
                <div className="flex items-center gap-2 rounded border p-2">
                  <code className="text-xs break-all">{c.delivery_url}</code>
                  <Button size="sm" variant="ghost" className="ml-auto" onClick={() => navigator.clipboard.writeText(c.delivery_url!)}>
                    Copy
                  </Button>
                </div>
              </section>
            )}
            <section className="space-y-2">
              <h2 className="font-semibold">Secret</h2>
              {c.auth_method === 'github_app' ? (
                <p className="text-muted-foreground">
                  None: it uses the Pliable GitHub app (Settings → Git). Each run gets a read-only token, for an hour, on only the repositories
                  its step lists.
                </p>
              ) : c.auth_method === 'linear_app' ? (
                <p className="text-muted-foreground">
                  The Pliable Linear app’s, installed in {String(c.config?.organization ?? 'your Linear workspace')}. Pliable keeps it current;
                  each run gets a short-lived token. To reconnect, delete this connection and install again.
                </p>
              ) : c.auth_method === 'oauth' ? (
                <p className="text-muted-foreground">
                  Signed in with OAuth. Pliable keeps the token current; each run gets a short-lived one. To reconnect, delete this connection
                  and sign in again.
                </p>
              ) : (
                <p className="text-muted-foreground">Stored encrypted and never shown again. Replace it by typing it again.</p>
              )}
              {!isAdmin || c.auth_method === 'github_app' || c.auth_method === 'linear_app' || c.auth_method === 'oauth' ? null : replacing ? (
                <SecretFields connector={c.connector} submitLabel="Replace" pending={replace.isPending} onSubmit={(s) => replace.mutate(s)} />
              ) : (
                <Button size="sm" variant="outline" onClick={() => setReplacing(true)}>
                  Replace secret
                </Button>
              )}
            </section>
            {c.connector === 'mcp' && <McpTools c={c} isAdmin={isAdmin} />}
            <section className="space-y-2">
              <h2 className="font-semibold">Used by</h2>
              {users.length ? (
                <ul className="space-y-1">
                  {users.map(({ team, how }) => (
                    <li key={team.id}>
                      <Link to={teamPath(team.id!, 'steps')} className="underline">
                        {team.name}
                      </Link>
                      <span className="text-muted-foreground">: {how.join(', ')}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">No agent uses it yet.</p>
              )}
            </section>
          </div>
        </div>
        <SetupGuide connector={c.connector} />
      </div>
    </div>
  );
}

export default function AgentConnections() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search] = useSearchParams();
  const [connector, setConnector] = useState(() => Object.keys(CONNECTORS).find((k) => k === search.get('connector')) ?? '');
  const [name, setName] = useState('');
  const [useApp, setUseApp] = useState(true);
  const [url, setUrl] = useState(''); // an MCP server's
  const { isAdmin } = useUserRoles();
  const list = useConnections();
  const create = useMutation({
    mutationFn: async ({ auth_method, secret }: { auth_method: string; secret: Record<string, string> }) =>
      (await api().request('POST', '/agent-connections', {
        connector, name: name.trim() || `ops-${connector}`, auth_method, secret, config: connector === 'mcp' ? { url: url.trim() } : {},
      })) as Connection,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['agent-connections'] });
      navigate(connectionPath(created.id));
    },
    onError: (e) => showToast.error(errorText(e, 'Could not create the connection.')),
  });

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <Header
        title="Connections"
        subtitle="The tools and services your agents work through. See Docs → Agent Connections for how to set up each one."
      />
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 overflow-auto">
          <div className="max-w-3xl p-6 space-y-6">
            <ul className="space-y-2">
              <li className="rounded border p-3 text-sm flex items-center gap-2">
                <Plug className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{PLIABLE}</span>
                <span className="text-muted-foreground">Pliable, built in: ask Ellie about the data</span>
              </li>
              {(list.data?.connections ?? []).map((c) => (
                <li key={c.id}>
                  <Link to={connectionPath(c.id)} className="rounded border p-3 text-sm flex items-center gap-2 hover:bg-accent/50">
                    <Plug className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{c.name}</span>
                    <span className="text-muted-foreground">{CONNECTORS[c.connector]?.label ?? c.connector}</span>
                  </Link>
                </li>
              ))}
            </ul>
            {isAdmin && <div className="rounded border p-4 space-y-3">
              <h2 className="text-sm font-semibold">Add a connection</h2>
              <div className="flex gap-2 items-end">
                <div className="space-y-1">
                  <Label>Connector</Label>
                  <Select value={connector} onValueChange={setConnector}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CONNECTORS).map(([key, c]) => (
                        <SelectItem key={key} value={key}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {connector && (
                  <div className="space-y-1">
                    <Label htmlFor="connection-name">Name (agents refer to it)</Label>
                    <Input id="connection-name" className="w-56" value={name} placeholder={`ops-${connector}`} onChange={(e) => setName(e.target.value)} />
                  </div>
                )}
                {connector === 'mcp' && (
                  <div className="space-y-1 flex-1">
                    <Label htmlFor="connection-url">Server URL</Label>
                    <Input id="connection-url" value={url} placeholder="https://mcp.example.com/mcp" onChange={(e) => setUrl(e.target.value)} />
                  </div>
                )}
              </div>
              {connector && (
                <>
                  <p className="text-xs text-muted-foreground">{CONNECTORS[connector]!.hint}</p>
                  {(connector === 'github' || connector === 'linear' || connector === 'mcp') && (
                    <div className="flex gap-2">
                      <Button size="sm" variant={useApp ? 'secondary' : 'ghost'} onClick={() => setUseApp(true)}>
                        {{ github: 'Pliable GitHub app', linear: 'Pliable Linear app', mcp: 'Sign in (OAuth)' }[connector]}
                      </Button>
                      <Button size="sm" variant={useApp ? 'ghost' : 'secondary'} onClick={() => setUseApp(false)}>
                        {connector === 'linear' ? 'API key' : 'Token'}
                      </Button>
                    </div>
                  )}
                  {connector === 'mcp' && useApp ? (
                    <McpSignIn name={name.trim() || `ops-${connector}`} url={url.trim()} />
                  ) : connector === 'linear' && useApp ? (
                    <LinearApp
                      existing={list.data?.connections.find((c) => c.auth_method === 'linear_app')}
                      name={name.trim() || `ops-${connector}`} />
                  ) : connector === 'github' && useApp ? (
                    <GithubApp
                      existing={list.data?.connections.find((c) => c.auth_method === 'github_app')}
                      pending={create.isPending}
                      onSubmit={() => create.mutate({ auth_method: 'github_app', secret: {} })} />
                  ) : (
                    <SecretFields
                      key={connector}
                      connector={connector}
                      submitLabel="Add connection"
                      pending={create.isPending}
                      onSubmit={(secret) => create.mutate({ auth_method: 'api_key', secret })}
                    />
                  )}
                </>
              )}
            </div>}
          </div>
        </div>
        <SetupGuide connector={connector} />
      </div>
    </div>
  );
}
